import type { FetchTicketSaleListParams } from 'Services/SalesManagement/Features/TicketSale/types'
import { parsePositiveIntegerOrUndefined } from 'Util/formatters'
import { useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

export const SELECTED_TICKET_SALE_PARAMETER_KEY = 'selectedTicketSale'

const useTicketSalesParams = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const params = useMemo<FetchTicketSaleListParams>(() => {
    const pageNumber = parsePositiveIntegerOrUndefined(searchParams.get('page')) ?? 1
    return {
      ordering: searchParams.get('ordering') ?? '-created',
      page: pageNumber
    }
  }, [searchParams])

  const selectedTicketSaleId = useMemo<string | undefined>(
    () => searchParams.get(SELECTED_TICKET_SALE_PARAMETER_KEY) ?? undefined,
    [searchParams]
  )

  const setParams = useCallback(
    (updater: (prev: FetchTicketSaleListParams) => FetchTicketSaleListParams, replace = false) => {
      setSearchParams(
        prev => {
          const pageNumber = parsePositiveIntegerOrUndefined(prev.get('page')) ?? 1
          const currentParams: FetchTicketSaleListParams = {
            ordering: prev.get('ordering') ?? '-created',
            page: pageNumber
          }
          const next = updater(currentParams)
          const newParams = new URLSearchParams(prev)
          Object.entries(next).forEach(([key, value]) => {
            if (value != null && value !== undefined) newParams.set(key, value.toString())
            else newParams.delete(key)
          })
          return newParams
        },
        { replace: replace }
      )
    },
    [setSearchParams]
  )

  const setSelectedTicketSaleId = useCallback(
    (id: string | undefined) => {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev)
        if (id != null && id !== undefined)
          newParams.set(SELECTED_TICKET_SALE_PARAMETER_KEY, id.toString())
        else newParams.delete(SELECTED_TICKET_SALE_PARAMETER_KEY)
        return newParams
      })
    },
    [setSearchParams]
  )

  // Run setParams on mount to ensure URL parameters are in sync with state
  // This is needed in case user navigates to the page with URL parameters already set
  useEffect(() => {
    setParams(prevState => ({ ...prevState }), true)
  }, [setParams])

  return {
    params,
    setParams,
    selectedTicketSaleId,
    setSelectedTicketSaleId
  }
}

export default useTicketSalesParams
